// src/LocationPicker.js
import * as React from "react";
import { useState } from "react";
import { MapContainer, TileLayer, Marker, useMapEvents, useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import IconButton from "@mui/material/IconButton";
import CircularProgress from "@mui/material/CircularProgress";
import SearchIcon from "@mui/icons-material/Search";
import InputAdornment from "@mui/material/InputAdornment";
import TextField from "@mui/material/TextField";

// Fix pentru iconul default Leaflet care nu se incarca corect cu bundler-ele moderne
// (webpack/CRA rescrie path-urile catre imagini din pachet)
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
    iconRetinaUrl: require("leaflet/dist/images/marker-icon-2x.png"),
    iconUrl: require("leaflet/dist/images/marker-icon.png"),
    shadowUrl: require("leaflet/dist/images/marker-shadow.png"),
});

const DEFAULT_CENTER = [46.7712, 23.5833]; // Cluj-Napoca, RO - centru implicit cand nu avem coordonate

const ClickHandler = ({ onPick }) => {
    useMapEvents({
        click(e) {
            onPick(e.latlng.lat, e.latlng.lng);
        },
    });
    return null;
};

// Componenta interna care recentreaza harta programatic cand "flyTo" se schimba
// (necesara pentru rezultatul cautarii de adresa - useMap() functioneaza doar
// in interiorul unui <MapContainer>)
const RecenterOnSearch = ({ flyTo }) => {
    const map = useMap();
    React.useEffect(() => {
        if (flyTo) {
            map.flyTo([flyTo.lat, flyTo.lng], 16);
        }
    }, [flyTo, map]);
    return null;
};

/**
 * Cauta o adresa prin Nominatim (OpenStreetMap) si returneaza prima potrivire.
 * Nominatim e gratuit dar cu rate-limit ~1 req/sec si necesita un User-Agent
 * identificabil conform politicii lor de utilizare.
 */
const cautaAdresaNominatim = async (query) => {
    const url = `https://nominatim.openstreetmap.org/search?format=json&limit=1&q=${encodeURIComponent(query)}`;
    const res = await fetch(url, {
        headers: { "Accept-Language": "ro" },
    });
    if (!res.ok) throw new Error("Eroare la căutarea adresei");
    const data = await res.json();
    if (!data.length) return null;
    return { lat: parseFloat(data[0].lat), lng: parseFloat(data[0].lon), displayName: data[0].display_name };
};

/**
 * Harta interactiva pentru selectarea unei locatii prin click sau cautare adresa.
 * Props:
 *  - latitudine, longitudine: valori curente (pot fi null)
 *  - adresa: textul adresei (folosit pentru cautare)
 *  - onChange(lat, lng): callback la click pe harta sau gasire adresa
 *  - height: inaltimea hartii in px (default 280)
 */
export const LocationPicker = ({ latitudine, longitudine, adresa, onChange, height = 280 }) => {
    const hasPosition = latitudine != null && longitudine != null;
    const center = hasPosition ? [latitudine, longitudine] : DEFAULT_CENTER;

    const [mapKey] = useState(() => `${center[0]}-${center[1]}-${Math.random()}`);
    const [searching, setSearching] = useState(false);
    const [flyTo, setFlyTo] = useState(null);
    const [searchError, setSearchError] = useState("");

    const handleSearch = async () => {
        if (!adresa || !adresa.trim()) return;
        setSearching(true);
        setSearchError("");
        try {
            const result = await cautaAdresaNominatim(adresa);
            if (!result) {
                setSearchError("Adresa nu a fost găsită");
            } else {
                onChange(result.lat, result.lng);
                setFlyTo({ lat: result.lat, lng: result.lng });
            }
        } catch (err) {
            setSearchError("Eroare la căutare, încearcă din nou");
        } finally {
            setSearching(false);
        }
    };

    return (
        <Box>
            <TextField
                fullWidth
                size="small"
                placeholder="Scrie adresa și apasă lupa pentru a o căuta pe hartă"
                value={adresa || ""}
                onChange={(e) => onChange(latitudine, longitudine, e.target.value)}
                onKeyDown={(e) => {
                    if (e.key === "Enter") {
                        e.preventDefault();
                        handleSearch();
                    }
                }}
                InputProps={{
                    endAdornment: (
                        <InputAdornment position="end">
                            <IconButton onClick={handleSearch} disabled={searching} size="small">
                                {searching ? <CircularProgress size={18} /> : <SearchIcon />}
                            </IconButton>
                        </InputAdornment>
                    ),
                }}
                sx={{ mb: 1 }}
            />
            {searchError && (
                <Typography variant="caption" color="error" sx={{ display: "block", mb: 1 }}>
                    {searchError}
                </Typography>
            )}
            <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: "block" }}>
                Sau click direct pe hartă pentru a seta locația
            </Typography>
            <Box sx={{ height, borderRadius: 1, overflow: "hidden", border: "1px solid #ddd" }}>
                <MapContainer
                    key={mapKey}
                    center={center}
                    zoom={hasPosition ? 14 : 12}
                    style={{ height: "100%", width: "100%" }}
                >
                    <TileLayer
                        attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                        url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                    />
                    {hasPosition && <Marker position={[latitudine, longitudine]} />}
                    <ClickHandler onPick={(lat, lng) => onChange(lat, lng)} />
                    <RecenterOnSearch flyTo={flyTo} />
                </MapContainer>
            </Box>
            {hasPosition && (
                <Typography variant="caption" color="text.secondary">
                    Lat: {latitudine.toFixed(5)}, Lng: {longitudine.toFixed(5)}
                </Typography>
            )}
        </Box>
    );
};
